import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { YaprioLogo } from "@/components/YaprioLogo";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6" data-testid="reset-password-page">
      <Card className="w-full max-w-md rounded-3xl border-slate-100 p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <YaprioLogo size={40} subtitle={false} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-slate-900">
            Reset your password
          </h1>
          <p className="mt-1 text-sm text-slate-500">Choose a new password to sign back in.</p>
        </div>

        {!token ? (
          <p className="rounded-xl bg-red-50 p-3 text-sm text-red-600" data-testid="reset-invalid-link">
            This reset link is missing a token. Request a new one from the login screen.
          </p>
        ) : done ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="reset-success">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="font-semibold text-slate-900">Password updated!</p>
            <p className="text-sm text-slate-500">Redirecting you to Yaprio…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  data-testid="input-new-password"
                  className="h-11 rounded-xl pl-9"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Confirm password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="input-confirm-password"
                  className="h-11 rounded-xl pl-9"
                />
              </div>
            </div>
            {error && (
              <p className="text-sm font-medium text-red-500" data-testid="reset-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="btn-reset-password"
              className="h-11 w-full rounded-full bg-pink-500 font-semibold text-white shadow-lg shadow-pink-500/30 hover:bg-pink-600"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}
        <p className="mt-5 text-center text-sm text-slate-500">
          <Link to="/" className="font-semibold text-pink-600 hover:text-pink-700">
            Back to Yaprio
          </Link>
        </p>
      </Card>
    </div>
  );
}
